import { findJuliaAttractingCycle, juliaEscapeHorizon, parseJuliaC } from './fractalIteration';

function tilePixelBounds(params) {
  const { xRange, yRange, canvasWidth, canvasHeight,
    startX, startY, regionWidth, regionHeight } = params || {};
  if (!Number.isInteger(canvasWidth) || !Number.isInteger(canvasHeight) ||
      !Number.isInteger(startX) || !Number.isInteger(startY) ||
      !Number.isInteger(regionWidth) || !Number.isInteger(regionHeight) ||
      canvasWidth < 1 || canvasHeight < 1 || canvasWidth > 16384 || canvasHeight > 16384 ||
      regionWidth < 1 || regionHeight < 1 || startX < 0 || startY < 0 ||
      startX + regionWidth > canvasWidth || startY + regionHeight > canvasHeight ||
      !Array.isArray(xRange) || !Array.isArray(yRange) ||
      xRange.length !== 2 || yRange.length !== 2) return null;
  const xMin = xRange[0], xMax = xRange[1];
  const yMin = yRange[0], yMax = yRange[1];
  if (!(Number.isFinite(xMin) && Number.isFinite(xMax) &&
        Number.isFinite(yMin) && Number.isFinite(yMax) &&
        xMax > xMin && yMax > yMin)) return null;
  const xScale = (xMax - xMin) / canvasWidth;
  const yScale = (yMax - yMin) / canvasHeight;
  if (!(Number.isFinite(xScale) && Number.isFinite(yScale) &&
        xScale > 0 && yScale > 0)) return null;
  const left = xMin + (startX + 0.5) * xScale;
  const right = xMin + (startX + regionWidth - 0.5) * xScale;
  const top = yMin + (startY + 0.5) * yScale;
  const bottom = yMin + (startY + regionHeight - 0.5) * yScale;
  if (!(Number.isFinite(left) && Number.isFinite(right) &&
        Number.isFinite(top) && Number.isFinite(bottom))) return null;
  return { xMin, xMax, yMin, yMax, left, right, top, bottom };
}

// Conservative CPU Float64 whole-tile zero proof; copied from the worker-only prototype.
export function isAnalyticZeroTile(params) {
  const bounds = tilePixelBounds(params);
  if (!bounds) return false;
  const { xMin, xMax, yMin, yMax, left, right, top, bottom } = bounds;
  if (!(xMin >= -4 && xMax <= 4 && yMin >= -4 && yMax <= 4)) return false;
  const rawJulia = params?.colorParams?.juliaC ?? params?.juliaC;
  if (rawJulia !== undefined && rawJulia !== null) {
    const julia = parseJuliaC(rawJulia);
    if (!julia) return false;
    const cycle = findJuliaAttractingCycle(julia);
    if (!cycle?.disks?.length) return false;
    for (const disk of cycle.disks) {
      const maxX = Math.max(Math.abs(left - disk.x), Math.abs(right - disk.x));
      const maxY = Math.max(Math.abs(top - disk.y), Math.abs(bottom - disk.y));
      if (maxX * maxX + maxY * maxY < disk.radiusSq - 2 ** -40) return true;
    }
    return false;
  }
  if (!(xMin <= 0.25 && xMax >= -1.25 && yMin <= 0.5 && yMax >= -0.5)) return false;
  const maxY = Math.max(Math.abs(top), Math.abs(bottom));
  const cardioidX = Math.max(Math.abs(left + 0.25), Math.abs(right + 0.25));
  if (cardioidX * cardioidX + maxY * maxY < 0.25 - 2 ** -40) return true;
  const bulbX = Math.max(Math.abs(left + 1), Math.abs(right + 1));
  return bulbX * bulbX + maxY * maxY < 0.0625 - 2 ** -40;
}

// Conservative whole-tile exterior escape horizon proof for Julia and parameter-plane views.
export function isAnalyticExteriorTile(params) {
  const bounds = tilePixelBounds(params);
  if (!bounds) return false;
  const { left, right, top, bottom } = bounds;
  const rawJulia = params?.colorParams?.juliaC ?? params?.juliaC;
  const horizon = rawJulia ? juliaEscapeHorizon(rawJulia) : 2;
  const minX = Math.max(0, left, -right);
  const minY = Math.max(0, top, -bottom);
  return minX * minX + minY * minY > horizon * horizon + 2 ** -38;
}
